// src/components/lesson/LessonView.jsx
import { useState, useEffect, useRef } from 'react';
import { lmsAPI, uploadFiles } from '../../services/api';
import QuizView    from './QuizView';
import SubmitView  from './SubmitView';

export default function LessonView({ lesson, enrollmentId, progress, onComplete }) {
  const lp     = progress?.find(p => p.id === lesson.id);
  const done   = lp?.status === 'completed';
  const [marking, setMarking] = useState(false);
  const [marked,  setMarked]  = useState(done);

  useEffect(() => {
    setMarked(progress?.find(p => p.id === lesson.id)?.status === 'completed');
  }, [lesson.id, progress]);

  const markComplete = async () => {
    setMarking(true);
    try {
      await lmsAPI.markComplete(lesson.id, { enrollment_id: enrollmentId });
      setMarked(true); onComplete();
    } catch { } finally { setMarking(false); }
  };

  // ── Text lesson ────────────────────────────────────────
  if (lesson.lesson_type === 'text') return (
    <div style={{ padding:'clamp(24px,5vw,60px)' }}>
      <LessonHeader lesson={lesson} done={marked}/>
      <div className="lesson-content" dangerouslySetInnerHTML={{ __html: lesson.content || '<p>Content coming soon.</p>' }} style={{ maxWidth:720, marginBottom:40 }}/>
      <MarkDoneBar done={marked} marking={marking} onMark={markComplete}/>
    </div>
  );

  // ── Video lesson ───────────────────────────────────────
  if (lesson.lesson_type === 'video') return (
    <div style={{ padding:'clamp(24px,5vw,60px)' }}>
      <LessonHeader lesson={lesson} done={marked}/>
      <VideoPlayer lesson={lesson} enrollmentId={enrollmentId}/>
      <MarkDoneBar done={marked} marking={marking} onMark={markComplete} style={{ marginTop:32 }}/>
    </div>
  );

  // ── File download ──────────────────────────────────────
  if (lesson.lesson_type === 'file') return (
    <div style={{ padding:'clamp(24px,5vw,60px)' }}>
      <LessonHeader lesson={lesson} done={marked}/>
      {lesson.file_url && (
        <a href={lesson.file_url} download={lesson.file_name} target="_blank" rel="noopener noreferrer"
          className="btn btn-outline" style={{ marginBottom:32, gap:10 }}
          onClick={() => setTimeout(markComplete, 1500)}>
          📎 Download {lesson.file_name || 'File'}
        </a>
      )}
      <MarkDoneBar done={marked} marking={marking} onMark={markComplete}/>
    </div>
  );

  // ── Live session ───────────────────────────────────────
  if (lesson.lesson_type === 'live_session') return (
    <div style={{ padding:'clamp(24px,5vw,60px)' }}>
      <LessonHeader lesson={lesson} done={marked}/>
      <div className="card" style={{ padding:28, maxWidth:560, marginBottom:32 }}>
        {lesson.live_datetime && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:'0.72rem', color:'var(--t-muted)', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:4 }}>Date & Time</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem' }}>
              {new Date(lesson.live_datetime).toLocaleDateString('en-RW',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
            </div>
            <div style={{ fontSize:'0.9rem', color:'var(--t-muted)' }}>
              {new Date(lesson.live_datetime).toLocaleTimeString('en-RW',{hour:'2-digit',minute:'2-digit'})}
              {lesson.live_duration ? ` · ${lesson.live_duration} minutes` : ''}
            </div>
          </div>
        )}
        {lesson.live_url && (
          <a href={lesson.live_url} target="_blank" rel="noopener noreferrer"
            className="btn btn-primary" style={{ width:'100%', padding:'13px', fontSize:'0.95rem', fontWeight:600 }}>
            🎥 Join Session →
          </a>
        )}
      </div>
      <MarkDoneBar done={marked} marking={marking} onMark={markComplete}/>
    </div>
  );

  // ── Quiz ───────────────────────────────────────────────
  if (lesson.lesson_type === 'quiz') return (
    <div style={{ padding:'clamp(24px,5vw,60px)' }}>
      <LessonHeader lesson={lesson} done={marked}/>
      <QuizView lesson={lesson} enrollmentId={enrollmentId} done={marked} onComplete={() => { setMarked(true); onComplete(); }}/>
    </div>
  );

  // ── Assignment / practical evidence / portfolio ────────
  if (['assignment','practical_evidence','portfolio'].includes(lesson.lesson_type)) return (
    <div style={{ padding:'clamp(24px,5vw,60px)' }}>
      <LessonHeader lesson={lesson} done={marked}/>
      {lesson.instructions && (
        <div style={{ background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:20, marginBottom:28, maxWidth:680 }}>
          <div style={{ fontSize:'0.7rem', color:'var(--gold)', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:8 }}>Instructions</div>
          <div style={{ fontSize:'0.95rem', color:'var(--t-secondary)', lineHeight:1.75 }} dangerouslySetInnerHTML={{ __html: lesson.instructions }}/>
        </div>
      )}
      <SubmitView lesson={lesson} enrollmentId={enrollmentId} done={marked} onComplete={() => { setMarked(true); onComplete(); }}/>
    </div>
  );

  return (
    <div style={{ padding:60, color:'var(--t-muted)' }}>Unknown lesson type: {lesson.lesson_type}</div>
  );
}

// ── Sub-components ────────────────────────────────────────

function LessonHeader({ lesson, done }) {
  const typeLabel = { text:'Reading', video:'Video', file:'Download', live_session:'Live Session', quiz:'Quiz', assignment:'Assignment', practical_evidence:'Practical Evidence', portfolio:'Portfolio Project' };
  return (
    <div style={{ marginBottom:28 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
        <span className="badge badge-dim" style={{ textTransform:'capitalize' }}>{typeLabel[lesson.lesson_type]||lesson.lesson_type}</span>
        {done && <span className="badge badge-green">✓ Completed</span>}
        {lesson.estimated_minutes && <span style={{ fontSize:'0.75rem', color:'var(--t-muted)' }}>{lesson.estimated_minutes} min</span>}
      </div>
      <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(1.4rem,3vw,2rem)', fontWeight:600, lineHeight:1.2, marginBottom:8 }}>
        {lesson.title}
      </h1>
    </div>
  );
}

function MarkDoneBar({ done, marking, onMark }) {
  if (done) return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 18px', borderRadius:'var(--r-md)', background:'rgba(62,207,142,0.1)', border:'1px solid rgba(62,207,142,0.25)', color:'var(--teal)', fontSize:'0.875rem', fontWeight:500 }}>
      ✓ Lesson completed
    </div>
  );
  return (
    <button className="btn btn-primary" onClick={onMark} disabled={marking}
      style={{ padding:'12px 28px', fontSize:'0.95rem', fontWeight:600 }}>
      {marking ? 'Saving…' : '✓ Mark as Complete'}
    </button>
  );
}

function VideoPlayer({ lesson, enrollmentId }) {
  const videoRef   = useRef(null);
  const lastSave   = useRef(0);

  const isYouTube  = lesson.video_url?.includes('youtube.com') || lesson.video_url?.includes('youtu.be');
  const isVimeo    = lesson.video_url?.includes('vimeo.com');

  const saveProgress = async (currentTime) => {
    if (!enrollmentId || currentTime - lastSave.current < 10) return;
    lastSave.current = currentTime;
    await lmsAPI.updateVideo(lesson.id, { enrollment_id: enrollmentId, video_progress: Math.floor(currentTime) }).catch(() => {});
  };

  if (isYouTube || isVimeo) {
    const embedUrl = isYouTube
      ? lesson.video_url.replace('watch?v=','embed/').replace('youtu.be/','youtube.com/embed/')
      : lesson.video_url.replace('vimeo.com/','player.vimeo.com/video/');
    return (
      <div style={{ aspectRatio:'16/9', borderRadius:'var(--r-lg)', overflow:'hidden', background:'#000', maxWidth:860, marginBottom:28 }}>
        <iframe src={embedUrl} style={{ width:'100%', height:'100%', border:'none' }} allowFullScreen title={lesson.title}/>
      </div>
    );
  }

  return (
    <div style={{ aspectRatio:'16/9', borderRadius:'var(--r-lg)', overflow:'hidden', background:'#000', maxWidth:860, marginBottom:28 }}>
      <video ref={videoRef} controls style={{ width:'100%', height:'100%', display:'block' }}
        onTimeUpdate={e => saveProgress(e.target.currentTime)}>
        <source src={lesson.video_url}/>
        Your browser does not support video.
      </video>
    </div>
  );
}
